import { useState } from "react";
import { Check, X } from "lucide-react";
import { Button, DialogFooter, Loader, Modal } from "@/ui/atoms";
import { formatDealStatusLabel } from "@/utils/formatDealStatus";
import type { TDeal } from "@/store";

type TDealStatusCellProps = {
    deal: TDeal;
    canToggle: boolean;
    onToggle?: (deal: TDeal) => void | Promise<void>;
};

const baseClass =
    "inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium";

export function DealStatusCell({ deal, canToggle, onToggle }: TDealStatusCellProps) {
    // Clicking the badge no longer toggles immediately — it opens a confirmation
    // modal that spells out what activating/deactivating the deal does (FIN-497).
    const [isConfirming, setIsConfirming] = useState(false);
    const [isToggling, setIsToggling] = useState(false);
    const [toggleError, setToggleError] = useState<string | null>(null);

    const colorClass = deal.isActive
        ? "bg-emerald-100 text-emerald-700"
        : "bg-slate-100 text-slate-600";

    // The pill carries the instant the deal entered its current status; the button's
    // title/aria-label stay the *action* ("Deactivate deal"), so the accessible name
    // is not diluted by a timestamp.
    const statusLabel = formatDealStatusLabel(deal);

    const content = deal.isActive ? (
        <>
            <Check className="h-3 w-3" />
            {statusLabel}
        </>
    ) : (
        <>
            <X className="h-3 w-3" />
            {statusLabel}
        </>
    );

    if (!canToggle) {
        return <span className={`${baseClass} ${colorClass}`}>{content}</span>;
    }

    const closeConfirm = () => {
        if (isToggling) return;
        setIsConfirming(false);
        setToggleError(null);
    };

    const confirmToggle = async () => {
        setIsToggling(true);
        setToggleError(null);
        try {
            await onToggle?.(deal);
            setIsConfirming(false);
        } catch (err) {
            setToggleError(err instanceof Error ? err.message : "Failed to update deal status");
        } finally {
            setIsToggling(false);
        }
    };

    return (
        <>
            <button
                type="button"
                data-testid="deal-active-toggle"
                className={`${baseClass} ${colorClass} transition-colors ${
                    deal.isActive ? "hover:bg-emerald-200" : "hover:bg-slate-200"
                }`}
                onClick={(e) => {
                    e.stopPropagation();
                    setToggleError(null);
                    setIsConfirming(true);
                }}
                title={deal.isActive ? "Deactivate deal" : "Activate deal"}
                aria-label={deal.isActive ? "Deactivate deal" : "Activate deal"}
            >
                {content}
            </button>
            {/* The dialog's DOM is portaled out, but React synthetic events still
                bubble through the REACT tree — this guard keeps clicks inside the
                dialog from reaching the row's onClick and navigating away. */}
            <span onClick={(e) => e.stopPropagation()}>
                <Modal
                    open={isConfirming}
                    onOpenChange={(open) => {
                        if (!open) closeConfirm();
                    }}
                    title={deal.isActive ? "Deactivate deal" : "Activate deal"}
                    description={
                        deal.isActive ? `Deactivate "${deal.name}"?` : `Activate "${deal.name}"?`
                    }
                    className="max-w-md"
                >
                    <div className="space-y-4">
                        {toggleError && (
                            <div className="rounded-lg bg-destructive/10 p-3 text-destructive text-sm">
                                {toggleError}
                            </div>
                        )}
                        <div className="rounded-lg bg-muted p-3 text-sm text-muted-foreground space-y-2">
                            {deal.isActive ? (
                                <>
                                    <p>
                                        <span className="font-medium text-foreground">
                                            {deal.name}
                                        </span>{" "}
                                        will no longer be selectable when uploading files.
                                    </p>
                                    <p>
                                        Its existing files, mappings and history are kept, and it
                                        can be reactivated at any time.
                                    </p>
                                </>
                            ) : (
                                <p>
                                    <span className="font-medium text-foreground">{deal.name}</span>{" "}
                                    will become selectable again on the upload form.
                                </p>
                            )}
                        </div>
                        <DialogFooter className="gap-2 pt-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={closeConfirm}
                                disabled={isToggling}
                            >
                                Cancel
                            </Button>
                            <Button
                                data-testid="deal-active-confirm"
                                type="button"
                                variant={deal.isActive ? "destructive" : "success"}
                                onClick={confirmToggle}
                                disabled={isToggling}
                            >
                                {isToggling ? (
                                    <>
                                        <Loader size="sm" className="mr-2" />
                                        {deal.isActive ? "Deactivating..." : "Activating..."}
                                    </>
                                ) : deal.isActive ? (
                                    "Deactivate deal"
                                ) : (
                                    "Activate deal"
                                )}
                            </Button>
                        </DialogFooter>
                    </div>
                </Modal>
            </span>
        </>
    );
}
